<template>
  <div class="h-100">
    <div class="overflow-scroll p-1">
      <div class="d-flex align-items-center mb-1">
        <div class="d-flex w-100 align-items-center">
          <b-button variant="outline-secondary" class="size-button-center" @click="$emit('slide_window')">
             <span><feather-icon icon="MenuIcon"/></span>
          </b-button>
    
          <b-input-group class="ml-1">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon"></feather-icon>
            </b-input-group-prepend>
            <b-form-input :placeholder="$t('ia.assistants.searchBy')" class="input-search-history" v-model="query"></b-form-input>
            <b-input-group-append is-text class="cursor-pointer" v-if="query && query.length > 0" @click="query = undefined">
              <feather-icon icon="XIcon"></feather-icon>
            </b-input-group-append>
          </b-input-group>
        </div>
        
        <b-button 
          variant="blue-button" 
          class="blue-button h-100 ml-1"
          @click="show_filters = true"
        >
          <feather-icon icon="FilterIcon"></feather-icon>
        </b-button>
      </div>

      <div>
        <h4>{{ $t('ia.assistants.personas') }}</h4>
        <p class="avenir-medium">{{ $t('ia.assistants.tailorDescription') }}</p>

        
        <p>{{ $t('ia.assistants.privatePersonas') }}</p>
        <div class="grid mb-2">
          <div class="container-box d-flex justify-content-center align-items-center flex-column container-to-add" @click="openAddAssistant">
            <b-button variant="button-add" class="button-add" @click="openAddAssistant"><feather-icon icon="PlusIcon"></feather-icon></b-button>
            <h5>{{ $t('ia.assistants.addNewPersona') }}</h5>
          </div>

          <!-- Loading skeletons for private assistants -->
          <template v-if="!loaded_service">
            <div v-for="n in 6" :key="'private-skeleton-' + n" class="container-box skeleton-card">
              <div class="p-3">
                <div class="d-flex align-items-center mb-2">
                  <b-skeleton type="avatar" size="40px" class="mr-2"></b-skeleton>
                  <div class="flex-grow-1">
                    <b-skeleton width="60%" height="16px" class="mb-1"></b-skeleton>
                    <b-skeleton width="40%" height="12px"></b-skeleton>
                  </div>
                </div>
                <b-skeleton height="12px" class="mb-1"></b-skeleton>
                <b-skeleton width="80%" height="12px" class="mb-2"></b-skeleton>
                <div class="d-flex justify-content-between align-items-center">
                  <b-skeleton width="50%" height="10px"></b-skeleton>
                </div>
              </div>
              <b-skeleton type="button" width="100%" height="44px"></b-skeleton>
            </div>
          </template>

          <!-- Actual private assistants -->
          <template v-else>
            <card-assistant 
              v-for="assistant in private_assistants" 
              :key="assistant.uuid" 
              :assistant="assistant" 
              @delete_assistant="deleteAssistantAction" 
              @edit_assistant="editAssistant" 
              :active_assistant_uuid="active_assistant_uuid"
              @update_active_assistant="updateActiveAssistant"
            ></card-assistant>
          </template>
        </div>
        
        <p>{{ $t('ia.assistants.publicPersonas') }}</p>
        <div class="grid">
          <!-- Loading skeletons for public assistants -->
          <template v-if="!loaded_service">
            <div v-for="n in 2" :key="'public-skeleton-' + n" class="container-box skeleton-card">
              <div class="p-3">
                <div class="d-flex align-items-center mb-2">
                  <b-skeleton type="avatar" size="40px" class="mr-2"></b-skeleton>
                  <div class="flex-grow-1">
                    <b-skeleton width="70%" height="16px" class="mb-1"></b-skeleton>
                    <b-skeleton width="50%" height="12px"></b-skeleton>
                  </div>
                </div>
                <b-skeleton height="12px" class="mb-1"></b-skeleton>
                <b-skeleton width="90%" height="12px" class="mb-1"></b-skeleton>
                <b-skeleton width="60%" height="12px" class="mb-2"></b-skeleton>
              </div>
              <b-skeleton type="button" width="100%" height="44px"></b-skeleton>
            </div>
          </template>

          <!-- Actual public assistants -->
          <template v-else>
            <card-assistant 
              v-for="assistant in public_assistants" 
              :key="assistant.uuid" 
              :assistant="assistant" 
              :is_public="true" 
              :active_assistant_uuid="active_assistant_uuid"
              @update_active_assistant="updateActiveAssistant"
            ></card-assistant>
          </template>
        </div>
      </div>
    </div>

    <b-sidebar right v-model="add_assistant" class="sidebar-add-assistant-brandme-ai" shadow backdrop :title="$t('ia.assistants.addAssistant')">
      <add-assistant @close_sidebar="updateAssistants" :assistant="editing_assistant" v-if="add_assistant"></add-assistant>
    </b-sidebar>

     <!-- Sidebar filters -->
    <b-sidebar right v-model="show_filters" class="sidebar-filters-brandme-ai" shadow backdrop :title="$t('ia.assistants.filters.title')">
      <filters-assistant 
        :initial_filters="current_filters"
        @apply-filters="onApplyFilters"
        @close-sidebar="show_filters = false"
      ></filters-assistant>
    </b-sidebar>
  </div>
</template>
    
<script>

import {
  BButton,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend,
  BSidebar,
  BSkeleton,
} from 'bootstrap-vue';
import { getAssistants, deleteAssistant } from '@/services/ia'
import { formatDateIA } from '@/libs/utils/times';
import { getIconWhite, getIconColor } from '@/libs/utils/icons';
import { showToast } from '@/libs/utils/others';
    
export default {
  name: 'ExplorePersonas',
  components: {
    BButton,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BSidebar,
    BSkeleton,
    CardAssistant: () => import('@/views/components/brandme-ai/cards/CardAssistant.vue'),
    AddAssistant: () => import('@/views/components/brandme-ai/forms/AddAssistant.vue'),
    FiltersAssistant: () => import('@/views/components/brandme-ai/siderbars/siderFilterAssistants.vue'),
  },
  props: {
    data_settings: {
      type: Object,
      default: null,
    }
  },
  data() {
    const DEFAULT_FILTERS = {
      formalities: ['formal','casual'],
      bias: ['neutral','opinionated'],
      response_length: ['short','long']
    };
    return {
      formatDateIA,
      getIconWhite,
      getIconColor,
      add_assistant: false,
      show_filters: false,
      query: undefined,
      loaded_service: false,
      public_assistants: [],
      private_assistants: [],
      current_filters: { ...DEFAULT_FILTERS },
      fetch_timeout: null,
      editing_assistant: null,
      active_assistant_uuid: null,
      DEFAULT_FILTERS,
    }
  },
  created() {
    this.fetchAll();
  },
  beforeDestroy() {
    if (this.fetch_timeout) {
      clearTimeout(this.fetch_timeout);
    }
  },
  methods: {
    fetchAll() {
      this.getPrivateAssistants();
      this.getPublicAssistants();
    },
    updateActiveAssistant(response) {
      this.active_assistant_uuid = response.person_assistant?.uuid;
      this.$emit('update-settings', response);
    },
    updateAssistants(assistant_data) {
      this.add_assistant = false;
      
      if (this.editing_assistant) {
        const index = this.private_assistants.findIndex(a => a.uuid === this.editing_assistant.uuid);
        if (index !== -1) {
          this.private_assistants.splice(index, 1, assistant_data);
        }
        this.editing_assistant = null;
      } else {
        this.private_assistants.unshift(assistant_data);
      }
    },
    openAddAssistant() {
      this.editing_assistant = null;
      this.add_assistant = true;
    },
    editAssistant(assistant) {
      this.editing_assistant = assistant;
      this.add_assistant = true;
    },
    deleteAssistantAction(person_assistant_uuid) {
      deleteAssistant(person_assistant_uuid).then(() => {
        this.private_assistants = this.private_assistants.filter(assistant => assistant.uuid !== person_assistant_uuid);
        showToast(this, 'success', 'SuccessIcon', 'ia.assistants.assistantDeleted');
      });
    },
    getPrivateAssistants() {
      const payload = this.buildRequestPayload();
      getAssistants(payload)
        .then(r => {
          this.private_assistants = r.results || [];
          this.loaded_service = true;
        })
        .catch(() => { this.loaded_service = true; });
    },
    getPublicAssistants() {
      const payload = this.buildRequestPayload();
      getAssistants(payload, false)
        .then(r => {
          this.public_assistants = r.results || [];
          this.loaded_service = true;
          this.active_assistant_uuid = this.data_settings ? this.data_settings.person_assistant.uuid : null;

        })
        .catch(() => { this.loaded_service = true; });
    },

    buildRequestPayload(overrides = {}) {
      const f = { ...this.current_filters, ...overrides };
      const payload = {
        formalities: f.formalities,
        bias: f.bias,
        response_length: f.response_length,
      };
      
      // add query only if not empty
      if (this.query && this.query.trim().length > 0) {
        payload.query = this.query.trim();
      }
      
      return payload;
    },
    onApplyFilters(finalFilters) {
      this.current_filters = { ...finalFilters };
      this.fetchAll();
      this.show_filters = false;
    },    
  },
  watch: {
    query(new_val, olv_val) {
      if (this.fetch_timeout) {
        clearTimeout(this.fetch_timeout);
      }
      
      if (new_val !== olv_val) {
        this.fetch_timeout = setTimeout(() => {
          this.fetchAll();
        }, 450);
      }
    }
  },
}
</script>
<style lang="scss" scoped>
.overflow-scroll {
  max-height: 100%;
  overflow-y: auto;
}
.grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media(max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media(max-width: 576px) {
    grid-template-columns: repeat(1, 1fr);
  }

  .container-to-add {
    &:hover {
      background-color: #e9ecef2b;
      cursor: pointer;
    }
  }

  .container-box {
    border: 1px solid rgb(229.5, 229.5, 229.5);
    border-radius: 5px;
    min-height: 190px;

    .ml-05 {
      margin-left: 0.5em;
    }
  
    .button-add {
      border-radius: 100% !important;
      height: 42px;
      width: 42px;
      padding: 0;
      margin-bottom: 0.5em;
      background-color: #e9ecef;
      color: #444;

      &:hover {
        background-color: #e9ecef75;
      }
    }
    .mb-05 {
      margin-bottom: 0.5em;
    }

    &.skeleton-card {
      background-color: #f8f9fa;
      
      .gap-1 {
        gap: 0.25rem;
      }
      
      // Animación sutil para los skeletons
      .b-skeleton {
        animation: skeleton-loading 1.5s ease-in-out infinite alternate;
      }
    }
  }
}

// Animación para los skeletons
@keyframes skeleton-loading {
  0% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
}


</style>
<style lang="scss">
.sidebar-add-assistant-brandme-ai {
  .b-sidebar { 
    width: 600px !important;
    background-color: white !important;

  }
}
</style>